<script lang="ts">
  import { onMount } from "svelte";
  import type { Bookmark } from "./bookmarks";
  import DialogConfigAzureBlob from "./DialogConfigAzureBlob.svelte";
  import DialogConfigAzureFiles from "./DialogConfigAzureFiles.svelte";
  import DialogConfigBackBlaze from "./DialogConfigBackBlaze.svelte";
  import DialogConfigBox from "./DialogConfigBox.svelte";
  import DialogConfigCloudinary from "./DialogConfigCloudinary.svelte";
  import DialogConfigDrime from "./DialogConfigDrime.svelte";
  import DialogConfigFichier from "./DialogConfigFichier.svelte";
  import DialogConfigFilelu from "./DialogConfigFilelu.svelte";
  import DialogConfigFilesCom from "./DialogConfigFilesCom.svelte";
  import DialogConfigFtp from "./DialogConfigFtp.svelte";
  import DialogConfigGcs from "./DialogConfigGcs.svelte";
  import DialogConfigGofile from "./DialogConfigGofile.svelte";
  import DialogConfigHttp from "./DialogConfigHttp.svelte";
  import DialogConfigImagekit from "./DialogConfigImagekit.svelte";
  import DialogConfigInternetArchive from "./DialogConfigInternetArchive.svelte";
  import DialogConfigJottacloud from "./DialogConfigJottacloud.svelte";
  import DialogConfigKoofr from "./DialogConfigKoofr.svelte";
  import DialogConfigLinkbox from "./DialogConfigLinkbox.svelte";
  import DialogConfigMega from "./DialogConfigMega.svelte";
  import DialogConfigOpendrive from "./DialogConfigOpendrive.svelte";
  import DialogConfigPcloud from "./DialogConfigPcloud.svelte";
  import DialogConfigPixeldrain from "./DialogConfigPixeldrain.svelte";
  import DialogConfigPremiumize from "./DialogConfigPremiumize.svelte";
  import DialogConfigPutio from "./DialogConfigPutio.svelte";
  import DialogConfigQuatrix from "./DialogConfigQuatrix.svelte";
  import DialogConfigS3 from "./DialogConfigS3.svelte";
  import DialogConfigSeafile from "./DialogConfigSeafile.svelte";
  import DialogConfigSftp from "./DialogConfigSftp.svelte";
  import DialogConfigSmb from "./DialogConfigSmb.svelte";
  import DialogConfigStorj from "./DialogConfigStorj.svelte";
  import DialogConfigSwift from "./DialogConfigSwift.svelte";
  import DialogConfigUlozto from "./DialogConfigUlozto.svelte";
  import DialogConfigWebdav from "./DialogConfigWebdav.svelte";
  import DialogConfigYandex from "./DialogConfigYandex.svelte";
  import FileListTopNav from "./FileListTopNav.svelte";
  import type { FS, FSConfig } from "./fs";
  import {
    createLocalConfig,
    createTestFilesConfig,
    fsConfigFullName,
    fsFullName,
    fsTypeDropbox,
    fsTypeFromFSConfig,
    fsTypeLocal,
    fsTypeOneDrive,
    fsTypeTestFiles,
  } from "./fs";
  import { connectDropbox } from "./fs-dropbox-login";
  import { connectGDrive, makeFileSystemGDrive } from "./fs-gdrive";
  import { makeFileSystemLocal } from "./fs-local";
  import { makeFileSystemFromConfig, openSavedLocalFolder } from "./fs-make";
  import { makeFileSystemOneDrive } from "./fs-onedrive";
  import { connectOneDrive } from "./fs-onedrive-login";
  import { makeFileSystemTest } from "./fs-testfiles";
  import { homeViewNextIdx } from "./home-view";
  import { showMessage, showTemporaryMessage } from "./message";
  import type { PaneNav } from "./nav-history";
  import { paneNavKey } from "./nav-history";
  import { mountedFileSystems, rememberLocalFolder, savedFsConfigs } from "./state.svelte";
  import { isDev, len } from "./util";

  interface Props {
    // take the keyboard focus when shown
    takefocus?: boolean;
    // going back and forward in the places of the pane
    nav?: PaneNav;
    gotobookmark: (b: Bookmark) => void;
    mountfs: (fs: FS) => void;
  }

  let { takefocus = false, nav, gotobookmark, mountfs }: Props = $props();

  let h = window.location.host;

  let showingDialogConfigBackBlaze = $state(false);
  let showingDialogConfigS3 = $state(false);
  let showingDialogConfigPutio = $state(false);
  let showingDialogConfigPixeldrain = $state(false);
  let showingDialogConfigStorj = $state(false);
  let showingDialogConfigSftp = $state(false);
  let showingDialogConfigFtp = $state(false);
  let showingDialogConfigSmb = $state(false);
  let showingDialogConfigWebdav = $state(false);
  let showingDialogConfigBox = $state(false);
  let showingDialogConfigAzureBlob = $state(false);
  let showingDialogConfigGcs = $state(false);
  let showingDialogConfigPcloud = $state(false);
  let showingDialogConfigMega = $state(false);
  let showingDialogConfigKoofr = $state(false);
  let showingDialogConfigSeafile = $state(false);
  let showingDialogConfigYandex = $state(false);
  let showingDialogConfigJottacloud = $state(false);
  let showingDialogConfigSwift = $state(false);
  let showingDialogConfigFichier = $state(false);
  let showingDialogConfigOpendrive = $state(false);
  let showingDialogConfigGofile = $state(false);
  let showingDialogConfigPremiumize = $state(false);
  let showingDialogConfigInternetArchive = $state(false);
  let showingDialogConfigAzureFiles = $state(false);
  let showingDialogConfigHttp = $state(false);
  let showingDialogConfigFilesCom = $state(false);
  let showingDialogConfigFilelu = $state(false);
  let showingDialogConfigImagekit = $state(false);
  let showingDialogConfigCloudinary = $state(false);
  let showingDialogConfigDrime = $state(false);
  let showingDialogConfigQuatrix = $state(false);
  let showingDialogConfigLinkbox = $state(false);
  let showingDialogConfigUlozto = $state(false);

  // we only support one of those
  let connectingGoogle = false;

  let connectedDropbox = $derived.by(() => {
    return mountedFileSystems.some((fs) => fsTypeFromFSConfig(fs.config) == fsTypeDropbox);
  });
  let connectedOneDrive = $derived.by(() => {
    return mountedFileSystems.some((fs) => fsTypeFromFSConfig(fs.config) == fsTypeOneDrive);
  });
  let showTestFiles = $derived.by(() => {
    if (!h.includes("localhost")) {
      return false;
    }
    return !mountedFileSystems.some((fs) => fsTypeFromFSConfig(fs.config) == fsTypeTestFiles);
  });

  async function pickLocalFolder() {
    let dirHandle;
    try {
      // @ts-ignore
      dirHandle = await window.showDirectoryPicker();
    } catch {
      console.log("pickLocalHolder: dismissed the folder pickup");
      return;
    }
    let c = createLocalConfig(dirHandle, dirHandle.name);
    // before the file system is made: the config may get the id of the saved
    // connection of this folder
    await rememberLocalFolder(c);
    const fs = makeFileSystemLocal(c);
    mountfs(fs);
  }

  function connectTestFiles() {
    let config = createTestFilesConfig();
    let fs = makeFileSystemTest(config);
    mountfs(fs);
  }

  async function onConnectGoogleDrive() {
    if (connectingGoogle) {
      return;
    }
    connectingGoogle = true;
    let remove = showMessage("connecting Google Drive");
    try {
      let config = await connectGDrive();
      mountfs(makeFileSystemGDrive(config));
    } catch (e) {
      console.error(e);
      remove();
      remove = () => {};
      let err = e as Error;
      showTemporaryMessage(err.message || String(e));
    } finally {
      remove();
      connectingGoogle = false;
    }
  }

  async function onConnectOneDrive() {
    let config = await connectOneDrive();
    console.log("onConnectedOneDrive: config:", config);
    const fs = makeFileSystemOneDrive(config);
    console.log("onConnected one drive: fs:", fs);
    mountfs(fs);
  }

  function onConnectDropbox() {
    connectDropbox(onConnected);
    function onConnected(fs: FS) {
      console.log("onConnected dropbox: fs:", fs);
      mountfs(fs);
    }
  }

  // @ts-ignore
  let hasFileSystemSupport = window.showDirectoryPicker;
  // per Sentry, Chrome Mobile 102 doesn't have storage.getDiretory function
  let hasStorage = !!(navigator.storage && navigator.storage.getDirectory);

  function mountFsConfig(c: FSConfig) {
    console.log("mountFsConfig", c);
    if (fsTypeFromFSConfig(c) == fsTypeLocal) {
      // the browser asks the user to let us use the folder again
      openSavedLocalFolder(c).then(mountfs, (e) => {
        console.error(e);
        showTemporaryMessage((e as Error).message || String(e));
      });
      return;
    }
    mountfs(makeFileSystemFromConfig(c));
  }

  // The Home View: what a pane shows when no file system is open in it.
  // Connected file systems come first, then saved connections, then the file
  // systems that can be connected. It is navigated like a file list: the
  // arrow keys select an item, Enter or a double-click opens it.
  // There is one selected item and no other highlight: moving the mouse over
  // an item selects it. A mouse that does not move leaves the selection to
  // the keys, also when the list scrolls under it.
  interface HomeItem {
    label: string;
    run: () => void;
  }
  interface HomeSection {
    title: string;
    // index of the first item among all items
    start: number;
    items: HomeItem[];
  }

  let homeSections = $derived.by(() => {
    let connected: HomeItem[] = mountedFileSystems.map((fs) => {
      return { label: fsFullName(fs), run: () => mountfs(fs) };
    });
    let saved: HomeItem[] = savedFsConfigs.map((c) => {
      return { label: fsConfigFullName(c), run: () => mountFsConfig(c) };
    });
    let connect: HomeItem[] = [];
    function add(label: string, run: () => void, show: any = true) {
      if (show) {
        connect.push({ label, run });
      }
    }
    // roughly by how widely used they are
    add("Local Folder", () => pickLocalFolder(), hasFileSystemSupport);
    add("S3", () => (showingDialogConfigS3 = true));
    add("Google Drive", () => onConnectGoogleDrive());
    add("Dropbox", () => onConnectDropbox(), !connectedDropbox);
    add("One Drive", () => onConnectOneDrive(), !connectedOneDrive);
    add("SFTP", () => (showingDialogConfigSftp = true));
    add("FTP", () => (showingDialogConfigFtp = true));
    add("WebDAV", () => (showingDialogConfigWebdav = true));
    add("SMB", () => (showingDialogConfigSmb = true));
    add("Box", () => (showingDialogConfigBox = true));
    add("Mega", () => (showingDialogConfigMega = true));
    add("pCloud", () => (showingDialogConfigPcloud = true));
    add("Google Cloud Storage", () => (showingDialogConfigGcs = true));
    add("Azure Blob Storage", () => (showingDialogConfigAzureBlob = true));
    add("Azure Files", () => (showingDialogConfigAzureFiles = true));
    add("BackBlaze", () => (showingDialogConfigBackBlaze = true), isDev());
    add("Yandex Disk", () => (showingDialogConfigYandex = true));
    add("Koofr", () => (showingDialogConfigKoofr = true));
    add("Seafile", () => (showingDialogConfigSeafile = true));
    add("Jottacloud", () => (showingDialogConfigJottacloud = true));
    add("Internet Archive", () => (showingDialogConfigInternetArchive = true));
    add("HTTP", () => (showingDialogConfigHttp = true));
    add("Storj", () => (showingDialogConfigStorj = true));
    add("OpenStack Swift", () => (showingDialogConfigSwift = true));
    add("Cloudinary", () => (showingDialogConfigCloudinary = true));
    add("ImageKit", () => (showingDialogConfigImagekit = true));
    add("Put.io", () => (showingDialogConfigPutio = true));
    add("premiumize.me", () => (showingDialogConfigPremiumize = true));
    add("1Fichier", () => (showingDialogConfigFichier = true));
    add("Pixeldrain", () => (showingDialogConfigPixeldrain = true));
    add("Gofile", () => (showingDialogConfigGofile = true));
    add("OpenDrive", () => (showingDialogConfigOpendrive = true));
    add("Files.com", () => (showingDialogConfigFilesCom = true));
    add("FileLu", () => (showingDialogConfigFilelu = true));
    add("Drime", () => (showingDialogConfigDrime = true));
    add("Quatrix", () => (showingDialogConfigQuatrix = true));
    add("Linkbox", () => (showingDialogConfigLinkbox = true));
    add("Uloz.to", () => (showingDialogConfigUlozto = true));
    add("Test Files", () => connectTestFiles(), showTestFiles);

    let res: HomeSection[] = [];
    let start = 0;
    for (let [title, list] of [
      ["Connected", connected],
      ["Saved Connections", saved],
      ["Connect Storage Service", connect],
    ] as [string, HomeItem[]][]) {
      if (len(list) > 0) {
        res.push({ title, start, items: list });
        start += len(list);
      }
    }
    return res;
  });
  let homeItems = $derived(homeSections.flatMap((s) => s.items));

  let elHome: HTMLElement = $state() as HTMLElement;
  let homeSelIdx = $state(0);
  // the list can get shorter under the selection
  let homeCurIdx = $derived(Math.max(0, Math.min(homeSelIdx, len(homeItems) - 1)));

  onMount(() => {
    // the selection starts at the first item
    if (takefocus) {
      elHome?.focus();
    }
  });

  function homeOpen(idx: number) {
    homeSelIdx = idx;
    homeItems[idx]?.run();
  }

  function onHomeKeyDown(ev: KeyboardEvent) {
    if (paneNavKey(ev, nav)) {
      return;
    }
    if (ev.target != elHome || ev.ctrlKey || ev.altKey || ev.metaKey) {
      return;
    }
    if (ev.key == "Enter") {
      ev.preventDefault();
      homeOpen(homeCurIdx);
      return;
    }
    let idx = homeViewNextIdx(ev.key, homeCurIdx, len(homeItems));
    if (idx < 0) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    homeSelIdx = idx;
    elHome?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }
</script>

<div class="max-h-full min-h-0 flex flex-col bg-white border-2 border-gray-300 focus-within:border-blue-600">
  <FileListTopNav path="Home View" {nav} {gotobookmark} />
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div
    bind:this={elHome}
    class="flex flex-col min-h-0 overflow-y-auto focus:outline-hidden"
    role="listbox"
    aria-label="Home View"
    tabindex="0"
    onkeydown={onHomeKeyDown}
  >
    {#each homeSections as section (section.title)}
      <div class="py-1 px-2 font-semibold text-center">{section.title}</div>
      {#each section.items as item, i}
        {@const idx = section.start + i}
        <!-- the list has the focus and the keys, not its items -->
        <!-- svelte-ignore a11y_click_events_have_key_events, a11y_interactive_supports_focus -->
        <div
          role="option"
          aria-selected={idx == homeCurIdx}
          data-idx={idx}
          class="home-item"
          class:bg-sky-200={idx == homeCurIdx}
          onmousemove={() => (homeSelIdx = idx)}
          ondblclick={() => homeOpen(idx)}
        >
          {item.label}
        </div>
      {/each}
    {/each}
  </div>
</div>

{#if showingDialogConfigBackBlaze}
  <DialogConfigBackBlaze bind:open={showingDialogConfigBackBlaze} {mountfs} />
{/if}
{#if showingDialogConfigS3}
  <DialogConfigS3 bind:open={showingDialogConfigS3} {mountfs} />
{/if}
{#if showingDialogConfigPutio}
  <DialogConfigPutio bind:open={showingDialogConfigPutio} {mountfs} />
{/if}
{#if showingDialogConfigPixeldrain}
  <DialogConfigPixeldrain bind:open={showingDialogConfigPixeldrain} {mountfs} />
{/if}
{#if showingDialogConfigStorj}
  <DialogConfigStorj bind:open={showingDialogConfigStorj} {mountfs} />
{/if}
{#if showingDialogConfigSftp}
  <DialogConfigSftp bind:open={showingDialogConfigSftp} {mountfs} />
{/if}
{#if showingDialogConfigSmb}
  <DialogConfigSmb bind:open={showingDialogConfigSmb} {mountfs} />
{/if}
{#if showingDialogConfigFtp}
  <DialogConfigFtp bind:open={showingDialogConfigFtp} {mountfs} />
{/if}
{#if showingDialogConfigWebdav}
  <DialogConfigWebdav bind:open={showingDialogConfigWebdav} {mountfs} />
{/if}
{#if showingDialogConfigBox}
  <DialogConfigBox bind:open={showingDialogConfigBox} {mountfs} />
{/if}
{#if showingDialogConfigAzureBlob}
  <DialogConfigAzureBlob bind:open={showingDialogConfigAzureBlob} {mountfs} />
{/if}
{#if showingDialogConfigGcs}
  <DialogConfigGcs bind:open={showingDialogConfigGcs} {mountfs} />
{/if}
{#if showingDialogConfigPcloud}
  <DialogConfigPcloud bind:open={showingDialogConfigPcloud} {mountfs} />
{/if}
{#if showingDialogConfigUlozto}
  <DialogConfigUlozto bind:open={showingDialogConfigUlozto} {mountfs} />
{/if}
{#if showingDialogConfigLinkbox}
  <DialogConfigLinkbox bind:open={showingDialogConfigLinkbox} {mountfs} />
{/if}
{#if showingDialogConfigQuatrix}
  <DialogConfigQuatrix bind:open={showingDialogConfigQuatrix} {mountfs} />
{/if}
{#if showingDialogConfigDrime}
  <DialogConfigDrime bind:open={showingDialogConfigDrime} {mountfs} />
{/if}
{#if showingDialogConfigCloudinary}
  <DialogConfigCloudinary bind:open={showingDialogConfigCloudinary} {mountfs} />
{/if}
{#if showingDialogConfigImagekit}
  <DialogConfigImagekit bind:open={showingDialogConfigImagekit} {mountfs} />
{/if}
{#if showingDialogConfigFilelu}
  <DialogConfigFilelu bind:open={showingDialogConfigFilelu} {mountfs} />
{/if}
{#if showingDialogConfigFilesCom}
  <DialogConfigFilesCom bind:open={showingDialogConfigFilesCom} {mountfs} />
{/if}
{#if showingDialogConfigHttp}
  <DialogConfigHttp bind:open={showingDialogConfigHttp} {mountfs} />
{/if}
{#if showingDialogConfigAzureFiles}
  <DialogConfigAzureFiles bind:open={showingDialogConfigAzureFiles} {mountfs} />
{/if}
{#if showingDialogConfigInternetArchive}
  <DialogConfigInternetArchive bind:open={showingDialogConfigInternetArchive} {mountfs} />
{/if}
{#if showingDialogConfigPremiumize}
  <DialogConfigPremiumize bind:open={showingDialogConfigPremiumize} {mountfs} />
{/if}
{#if showingDialogConfigGofile}
  <DialogConfigGofile bind:open={showingDialogConfigGofile} {mountfs} />
{/if}
{#if showingDialogConfigOpendrive}
  <DialogConfigOpendrive bind:open={showingDialogConfigOpendrive} {mountfs} />
{/if}
{#if showingDialogConfigFichier}
  <DialogConfigFichier bind:open={showingDialogConfigFichier} {mountfs} />
{/if}
{#if showingDialogConfigSwift}
  <DialogConfigSwift bind:open={showingDialogConfigSwift} {mountfs} />
{/if}
{#if showingDialogConfigJottacloud}
  <DialogConfigJottacloud bind:open={showingDialogConfigJottacloud} {mountfs} />
{/if}
{#if showingDialogConfigYandex}
  <DialogConfigYandex bind:open={showingDialogConfigYandex} {mountfs} />
{/if}
{#if showingDialogConfigSeafile}
  <DialogConfigSeafile bind:open={showingDialogConfigSeafile} {mountfs} />
{/if}
{#if showingDialogConfigKoofr}
  <DialogConfigKoofr bind:open={showingDialogConfigKoofr} {mountfs} />
{/if}
{#if showingDialogConfigMega}
  <DialogConfigMega bind:open={showingDialogConfigMega} {mountfs} />
{/if}

<style lang="postcss">
  @reference "tailwindcss";
  /* the font, the size and the row height of a row of the file list */
  .home-item {
    @apply text-left cursor-pointer select-none truncate shrink-0;
    font-family: var(--font-monospace);
    font-size: 10pt;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
</style>
